import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { EmployeeGlAccountKey } from '../models/employee-account/employee-account';
import {
  ClaimPolicyOption,
  EXPENSE_CLAIM_ENTITY_CODE,
  PettyCashFund,
  PettyCashFundBalance,
  PettyCashFundSearchCommand,
  PettyCashFundSummary,
  PettyCashFundUpdate,
  UserOption,
} from '../models/petty-cash-fund/petty-cash-fund';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class PettyCashFundProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/petty-cash-fund';
  private readonly policyUrl = environment.proxyUrls.userMasterApi + '/api/v1/approval-policy';

  public search(
    command: PettyCashFundSearchCommand,
    pageNumber: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: number,
  ): Observable<ApiResponse<PagedResult<PettyCashFund>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<PettyCashFund>>>(`${this.baseUrl}/search`, command, { params });
  }

  public getSummary(): Observable<ApiResponse<PettyCashFundSummary>> {
    return this.httpClient.get<ApiResponse<PettyCashFundSummary>>(`${this.baseUrl}/summary`);
  }

  public getById(id: string): Observable<ApiResponse<PettyCashFund>> {
    return this.httpClient.get<ApiResponse<PettyCashFund>>(`${this.baseUrl}/${id}`);
  }

  public getBalance(id: string): Observable<ApiResponse<PettyCashFundBalance>> {
    return this.httpClient.get<ApiResponse<PettyCashFundBalance>>(`${this.baseUrl}/${id}/balance`);
  }

  public getAssignableUsers(): Observable<ApiResponse<UserOption[]>> {
    return this.httpClient.get<ApiResponse<UserOption[]>>(`${this.baseUrl}/assignable-users`);
  }

  /** The configured fund cash account of the session site's company, to prefill a new fund. */
  public getDefaultGlAccount(): Observable<ApiResponse<EmployeeGlAccountKey>> {
    return this.httpClient.get<ApiResponse<EmployeeGlAccountKey>>(`${this.baseUrl}/default-gl-account`);
  }

  public create(payload: PettyCashFundUpdate): Observable<ApiResponse<PettyCashFund>> {
    return this.httpClient.post<ApiResponse<PettyCashFund>>(this.baseUrl, payload);
  }

  public update(id: string, payload: PettyCashFundUpdate): Observable<ApiResponse<PettyCashFund>> {
    return this.httpClient.put<ApiResponse<PettyCashFund>>(`${this.baseUrl}/${id}`, payload);
  }

  /** Hands a Draft fund to the treasurer (#5786); locks its currency and account. */
  public markReadyForFunding(id: string): Observable<ApiResponse<PettyCashFund>> {
    return this.httpClient.post<ApiResponse<PettyCashFund>>(`${this.baseUrl}/${id}/ready-for-funding`, {});
  }

  public delete(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
  }

  /** Active UserMaster policies that may approve expense claims (needs ApprovalPolicyViewer). */
  public getClaimPolicies(): Observable<ApiResponse<PagedResult<ClaimPolicyOption>>> {
    const params = new HttpParams().set('pageNumber', 1).set('pageSize', 200).set('sortField', 'name').set('sortOrder', 1);
    return this.httpClient.post<ApiResponse<PagedResult<ClaimPolicyOption>>>(
      `${this.policyUrl}/search`,
      { isActive: true, appliesTo: [EXPENSE_CLAIM_ENTITY_CODE] },
      { params },
    );
  }
}
